/**
 * Sidebar Component
 * Navigation menu with permission-based menu items.
 * Supports mini variant (icon-only) collapse on desktop and
 * grouped/collapsible admin sections for non-master tenants.
 */

import { useEffect, Fragment } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  Box,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Collapse,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Description as ContractsIcon,
  People as PeopleIcon,
  Storage as DataIcon,
  Assessment as ReportsIcon,
  ManageAccounts as UsersIcon,
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  Business as TenantIcon,
  Email as EmailIcon,
  PictureAsPdf as PdfIcon,
  MonitorHeart as ServiceStatusIcon,
  CurrencyExchange as ApiSettingsIcon,
  Folder as DocumentsIcon,
  Category as DocumentTypesIcon,
  Approval as ApprovalIcon,
  PersonAdd as PendingUsersIcon,
  OpenInNew as ExternalLinkIcon,
  AdminPanelSettings as KeycloakIcon,
  Security as RolesIcon,
  Functions as CalculationProfilesIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  Group as GroupUsersIcon,
  Tune as GroupConfigIcon,
  Forum as GroupCommsIcon,
  Menu as MenuIcon,
  MergeType as MergeContractsIcon,
} from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';
import { usePermissions } from '../../contexts/PermissionContext';
import { MODULES, PERMISSION_LEVELS } from '../../contexts/PermissionConstants';
import { useTenant } from '../../contexts/TenantContext';
import { ADMIN_GROUPS } from '../../hooks/useSidebarState';

const groupIcons = {
  users_access: <GroupUsersIcon />,
  configuration: <GroupConfigIcon />,
  communications: <GroupCommsIcon />,
};

// Dark sidebar styling — accepts background color from theme
const getDarkDrawerPaper = (sidebarBg) => ({
  backgroundColor: sidebarBg,
  color: 'rgba(255,255,255,0.87)',
  '& .MuiListItemIcon-root': { color: 'rgba(255,255,255,0.7)' },
  '& .MuiListItemText-root': { color: 'rgba(255,255,255,0.87)' },
  '& .MuiListItemButton-root': {
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
    '&.Mui-selected': {
      backgroundColor: 'rgba(255,255,255,0.12)',
      '& .MuiListItemIcon-root': { color: '#ffffff' },
      '& .MuiListItemText-root': { color: '#ffffff' },
      '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
    },
  },
  '& .MuiDivider-root': { borderColor: 'rgba(255,255,255,0.12)' },
});

function Sidebar({ drawerWidth, collapsedWidth, mobileOpen, handleDrawerToggle, sidebarState }) {
  const navigate = useNavigate();
  const location = useLocation();
  const muiTheme = useTheme();
  const { hasPermission } = usePermissions();
  const { isModuleEnabled, tenant } = useTenant();
  const sidebarBg = muiTheme.palette.background.sidebar || '#1e293b';
  const darkDrawerPaper = getDarkDrawerPaper(sidebarBg);
  const { collapsed, toggleCollapsed, toggleSection, isSectionExpanded, autoExpandForPath } = sidebarState;

  // Server-authoritative (isMasterTenantCode in the API's requireOperatingTenant
  // middleware) rather than a client-side code-string comparison, so the sidebar can
  // never blank out the business menu because the client guessed the tenant code.
  const isMasterTenant = tenant?.isMaster === true;

  const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8080';
  const keycloakAdminUrl = `${keycloakUrl}/admin/master/console/`;

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/', module: MODULES.CAPITAL_MANAGEMENT, level: PERMISSION_LEVELS.READ },
    { text: 'Contracts', icon: <ContractsIcon />, path: '/contracts', module: MODULES.CONTRACT_MANAGEMENT, level: PERMISSION_LEVELS.READ },
    { text: 'Matching', icon: <PaymentIcon />, path: '/payments', module: MODULES.PAYMENTS, level: PERMISSION_LEVELS.WRITE },
    { text: 'Approvals', icon: <ApprovalIcon />, path: '/approvals', module: MODULES.CONTRACT_MANAGEMENT, level: PERMISSION_LEVELS.READ },
    { text: 'Stakeholders', icon: <PeopleIcon />, path: '/stakeholders', module: MODULES.STAKEHOLDER_MANAGEMENT, level: PERMISSION_LEVELS.READ },
    { text: 'Reports', icon: <ReportsIcon />, path: '/reports', module: MODULES.REPORTS, level: PERMISSION_LEVELS.READ },
    { text: 'Documents', icon: <DocumentsIcon />, path: '/admin/documents', module: MODULES.CONTRACT_MANAGEMENT, level: PERMISSION_LEVELS.WRITE },
  ];

  const masterPriorityItems = [
    { text: 'Service Status', icon: <ServiceStatusIcon />, path: '/admin/service-status', module: MODULES.ADMIN_SETTINGS, level: PERMISSION_LEVELS.ADMIN },
    { text: 'Pending Users', icon: <PendingUsersIcon />, path: '/admin/pending-users', module: MODULES.USER_MANAGEMENT, level: PERMISSION_LEVELS.ADMIN },
    { text: 'User Management', icon: <UsersIcon />, path: '/users', module: MODULES.USER_MANAGEMENT, level: PERMISSION_LEVELS.ADMIN },
    { text: 'Role Management', icon: <RolesIcon />, path: '/admin/roles', module: MODULES.USER_MANAGEMENT, level: PERMISSION_LEVELS.ADMIN },
    { text: 'Tenant Management', icon: <TenantIcon />, path: '/admin/tenants', module: MODULES.TENANT_MANAGEMENT, level: PERMISSION_LEVELS.ADMIN },
  ];

  const otherAdminItems = [
    { text: 'Reference Data', icon: <DataIcon />, path: '/reference-data', module: MODULES.REFERENCE_DATA, level: PERMISSION_LEVELS.ADMIN },
    { text: 'Calculation Profiles', icon: <CalculationProfilesIcon />, path: '/admin/calculation-profiles', module: MODULES.REFERENCE_DATA, level: PERMISSION_LEVELS.ADMIN },
    // Document Types moved into Reference Data (T3-039) — managed as a tab there.
    { text: 'Workflow Doc Requirements', icon: <DocumentTypesIcon />, path: '/admin/workflow-document-requirements', module: MODULES.WORKFLOW_DOCUMENT_REQUIREMENTS, level: PERMISSION_LEVELS.READ },
    { text: 'API Settings', icon: <ApiSettingsIcon />, path: '/admin/api-settings', module: MODULES.ADMIN_SETTINGS, level: PERMISSION_LEVELS.ADMIN },
    { text: 'Email Settings', icon: <EmailIcon />, path: '/admin/email-settings', module: MODULES.ADMIN_SETTINGS, level: PERMISSION_LEVELS.ADMIN },
    { text: 'Email Recipients', icon: <EmailIcon />, path: '/admin/email-recipients', module: MODULES.ADMIN_SETTINGS, level: PERMISSION_LEVELS.ADMIN },
    { text: 'Email Audit', icon: <EmailIcon />, path: '/admin/email-audit', module: MODULES.ADMIN_SETTINGS, level: PERMISSION_LEVELS.ADMIN },
    { text: 'Email Templates', icon: <EmailIcon />, path: '/admin/email-templates', module: MODULES.ADMIN_SETTINGS, level: PERMISSION_LEVELS.ADMIN },
    { text: 'PDF Templates', icon: <PdfIcon />, path: '/admin/pdf-templates', module: MODULES.ADMIN_SETTINGS, level: PERMISSION_LEVELS.ADMIN },
    { text: 'Tenant Settings', icon: <SettingsIcon />, path: '/admin/settings', module: MODULES.ADMIN_SETTINGS, level: PERMISSION_LEVELS.ADMIN },
    { text: 'Merge Historical Contracts', icon: <MergeContractsIcon />, path: '/admin/merge-contracts', module: MODULES.CONTRACT_MANAGEMENT, level: PERMISSION_LEVELS.ADMIN },
  ];

  // Master-only global admin items (stored in master DB, not tenant-scoped)
  const masterGlobalItems = [
    { text: 'API Settings', icon: <ApiSettingsIcon />, path: '/admin/api-settings', module: MODULES.ADMIN_SETTINGS, level: PERMISSION_LEVELS.ADMIN },
  ];

  const adminItems = isMasterTenant
    ? [...masterPriorityItems, ...masterGlobalItems]
    : [
        { text: 'Service Status', icon: <ServiceStatusIcon />, path: '/admin/service-status', module: MODULES.ADMIN_SETTINGS, level: PERMISSION_LEVELS.ADMIN },
        { text: 'User Management', icon: <UsersIcon />, path: '/users', module: MODULES.USER_MANAGEMENT, level: PERMISSION_LEVELS.ADMIN },
        { text: 'Role Management', icon: <RolesIcon />, path: '/admin/roles', module: MODULES.USER_MANAGEMENT, level: PERMISSION_LEVELS.ADMIN },
        ...otherAdminItems,
        { text: 'Tenant Management', icon: <TenantIcon />, path: '/admin/tenants', module: MODULES.TENANT_MANAGEMENT, level: PERMISSION_LEVELS.ADMIN },
      ];

  const handleNavigation = (path) => {
    navigate(path);
    if (mobileOpen) handleDrawerToggle();
  };

  // Auto-expand the admin group containing the current route
  useEffect(() => {
    autoExpandForPath(location.pathname, adminItems);
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  // The main menu items (Dashboard, Contracts, Matching, Approvals, Stakeholders,
  // Reports, Documents) are all operating-tenant business routes — the API blocks
  // them on the master tenant via requireOperatingTenant. Hide them on master so
  // the sidebar only surfaces the master/admin items below.
  const filteredMenuItems = isMasterTenant
    ? []
    : menuItems.filter(
        (item) => isModuleEnabled(item.module) && hasPermission(item.module, item.level)
      );

  const filteredAdminItems = adminItems.filter(
    (item) => isModuleEnabled(item.module) && hasPermission(item.module, item.level)
  );

  const hasAdminItems = filteredAdminItems.length > 0;
  const hasKeycloakPermission = hasPermission(MODULES.USER_MANAGEMENT, PERMISSION_LEVELS.ADMIN);

  // --- Render helpers ---

  const renderMenuItemButton = (item, isSelected, extraSx = {}) => (
    <ListItemButton
      selected={isSelected}
      onClick={() => handleNavigation(item.path)}
      sx={{
        minHeight: 48,
        justifyContent: collapsed ? 'center' : 'initial',
        px: 2.5,
        ...extraSx,
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: 0,
          mr: collapsed ? 0 : 3,
          justifyContent: 'center',
        }}
      >
        {item.icon}
      </ListItemIcon>
      {!collapsed && (
        <ListItemText primary={item.text} primaryTypographyProps={{ noWrap: true }} />
      )}
    </ListItemButton>
  );

  const renderMenuItem = (item, isSelected, extraSx) => {
    if (collapsed) {
      return (
        <Tooltip key={item.text} title={item.text} placement="right" arrow>
          <ListItem disablePadding sx={{ display: 'block' }}>
            {renderMenuItemButton(item, isSelected, extraSx)}
          </ListItem>
        </Tooltip>
      );
    }
    return (
      <ListItem key={item.text} disablePadding>
        {renderMenuItemButton(item, isSelected, extraSx)}
      </ListItem>
    );
  };

  const renderKeycloakLink = () => {
    const button = (
      <ListItemButton
        component="a"
        href={keycloakAdminUrl}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          minHeight: 48,
          justifyContent: collapsed ? 'center' : 'initial',
          px: 2.5,
        }}
      >
        <ListItemIcon sx={{ minWidth: 0, mr: collapsed ? 0 : 3, justifyContent: 'center' }}>
          <KeycloakIcon />
        </ListItemIcon>
        {!collapsed && (
          <>
            <ListItemText primary="Keycloak Admin" primaryTypographyProps={{ noWrap: true }} />
            <ExternalLinkIcon fontSize="small" sx={{ ml: 1, opacity: 0.6, flexShrink: 0 }} />
          </>
        )}
      </ListItemButton>
    );

    if (collapsed) {
      return (
        <Tooltip key="keycloak" title="Keycloak Admin" placement="right" arrow>
          <ListItem disablePadding sx={{ display: 'block' }}>
            {button}
          </ListItem>
        </Tooltip>
      );
    }
    return (
      <ListItem key="keycloak" disablePadding>
        {button}
      </ListItem>
    );
  };

  const renderAdminGroups = () => {
    const allGroupedTexts = ADMIN_GROUPS.flatMap((g) => g.items);

    return (
      <>
        {ADMIN_GROUPS.map((group) => {
          const groupItems = filteredAdminItems.filter((item) => group.items.includes(item.text));
          if (groupItems.length === 0) return null;

          const isExpanded = isSectionExpanded(group.key);
          const hasActiveItem = groupItems.some((item) => location.pathname === item.path);

          if (collapsed) {
            return (
              <Tooltip key={group.key} title={group.label} placement="right" arrow>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    selected={hasActiveItem}
                    onClick={() => {
                      toggleCollapsed();
                      if (!isExpanded) toggleSection(group.key);
                    }}
                    sx={{ minHeight: 48, justifyContent: 'center', px: 2.5 }}
                  >
                    <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                      {groupIcons[group.key]}
                    </ListItemIcon>
                  </ListItemButton>
                </ListItem>
              </Tooltip>
            );
          }

          return (
            <Fragment key={group.key}>
              <ListItemButton onClick={() => toggleSection(group.key)} sx={{ pl: 2, pr: 1 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {groupIcons[group.key]}
                </ListItemIcon>
                <ListItemText
                  primary={group.label}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: 600, color: 'rgba(255,255,255,0.5)', noWrap: true }}
                />
                {isExpanded
                  ? <ExpandMoreIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />
                  : <ChevronRightIcon fontSize="small" sx={{ color: 'rgba(255,255,255,0.5)' }} />
                }
              </ListItemButton>
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {groupItems.map((item) =>
                    renderMenuItem(item, location.pathname === item.path, { pl: 3 })
                  )}
                </List>
              </Collapse>
            </Fragment>
          );
        })}

        {/* Ungrouped fallback — renders any admin item not assigned to a group */}
        {filteredAdminItems
          .filter((item) => !allGroupedTexts.includes(item.text))
          .map((item) => renderMenuItem(item, location.pathname === item.path))}
      </>
    );
  };

  const collapseToggle = (
    <Box sx={{ mt: 'auto', borderTop: 1, borderColor: 'rgba(255,255,255,0.12)' }}>
      <ListItemButton
        onClick={toggleCollapsed}
        sx={{
          minHeight: 48,
          justifyContent: collapsed ? 'center' : 'flex-end',
          px: 2.5,
        }}
      >
        {collapsed ? <ChevronRightIcon sx={{ color: 'rgba(255,255,255,0.7)' }} /> : <ChevronLeftIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}
      </ListItemButton>
    </Box>
  );

  // --- Drawer content ---

  // Mobile: always full width, no collapse logic
  const mobileDrawerContent = (
    <div>
      <Toolbar />
      <Divider />
      {!isMasterTenant && (
        <List>
          {filteredMenuItems.map((item) => (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                selected={location.pathname === item.path}
                onClick={() => handleNavigation(item.path)}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
      {hasAdminItems && (
        <>
          <Divider />
          <List>
            <ListItem>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>ADMINISTRATION</Typography>
            </ListItem>
            {renderAdminGroups()}
            {isMasterTenant && hasKeycloakPermission && renderKeycloakLink()}
          </List>
        </>
      )}
    </div>
  );

  // Desktop: supports mini variant collapse and grouped admin sections
  const desktopDrawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Toolbar area — matches AppBar height, uses sidebar background */}
      <Toolbar
        onClick={toggleCollapsed}
        disableGutters
        sx={{
          justifyContent: collapsed ? 'center' : 'flex-start',
          minHeight: 64,
          px: 2.5,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
        }}
      >
        <Box sx={{ minWidth: 0, display: 'flex', justifyContent: 'center', mr: collapsed ? 0 : 3 }}>
          <MenuIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />
        </Box>
        {!collapsed && (
          <Typography variant="body1" sx={{ fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>Menu</Typography>
        )}
      </Toolbar>
      <Divider />

      <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
        {!isMasterTenant && (
          <List>
            {filteredMenuItems.map((item) =>
              renderMenuItem(item, location.pathname === item.path)
            )}
          </List>
        )}

        {hasAdminItems && (
          <>
            <Divider />
            <List>
              {!collapsed && (
                <ListItem sx={{ py: 0.5 }}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>ADMINISTRATION</Typography>
                </ListItem>
              )}
              {renderAdminGroups()}
              {isMasterTenant && hasKeycloakPermission && renderKeycloakLink()}
            </List>
          </>
        )}
      </Box>

      {collapseToggle}
    </Box>
  );

  return (
    <Box
      component="nav"
      sx={{
        width: { sm: collapsed ? collapsedWidth : drawerWidth },
        flexShrink: { sm: 0 },
        transition: (theme) =>
          theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
      }}
    >
      {/* Mobile drawer — temporary, always full width */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            ...darkDrawerPaper,
          },
        }}
      >
        {mobileDrawerContent}
      </Drawer>

      {/* Desktop drawer — permanent, supports mini variant */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: collapsed ? collapsedWidth : drawerWidth,
            overflowX: 'hidden',
            ...darkDrawerPaper,
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.leavingScreen,
              }),
          },
        }}
        open
      >
        {desktopDrawerContent}
      </Drawer>
    </Box>
  );
}

export default Sidebar;
